import {
    useState,
    useCallback,
} from 'react';

import { InteractionType } from '@azure/msal-browser';
import { useMsal, useMsalAuthentication } from "@azure/msal-react";
import { isInIframe } from '../utils/authUtils';
import { useFramedAuth } from '../auth/FramedAuthContext';

/**
 * Custom hook to call a web API using bearer token obtained from MSAL
 * @param {PopupRequest} msalRequest 
 * @returns 
 */
const useFetchWithMsal = (msalRequest) => {
    const { instance } = useMsal();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const framedAuth = useFramedAuth();

    const { error: msalError } = useMsalAuthentication(InteractionType.Silent, {
        ...msalRequest,
        account: instance.getActiveAccount(),
        redirectUri: '/'
    });

    const reauthenticate = () => (isInIframe() ? instance.loginPopup(msalRequest) : instance.loginRedirect(msalRequest));

    /**
     * Execute a fetch request with the given options
     * @param {string} method: GET, POST, PUT, DELETE
     * @param {String} endpoint: The endpoint to call
     * @param {Object} data: The data to send to the endpoint, if any 
     * @returns JSON response
     */
    const execute = async (method, endpoint, data = null) => {
        let accessToken;

        if (framedAuth?.accessToken) {
            accessToken = framedAuth.accessToken;
        } else {
            if (msalError) {
                setError(msalError);
                return;
            }

             // MsalProvider kicks off instance.initialize() asynchronously on mount, so on a hard
            // page refresh a fetch triggered early (e.g. on a deep-linked skill path route) can
            // race ahead of it. initialize() is a no-op once already initialized, so awaiting it
            // here is safe and guarantees the instance is ready before we call any other MSAL API.
           await instance.initialize();

            const activeAccount = instance.getActiveAccount();
            if (!activeAccount) {
                console.warn('MSAL: no active account, redirecting to login');
                reauthenticate();
                return;
            }

            try {
                const tokenResponse = await instance.acquireTokenSilent({
                    ...msalRequest,
                    account: activeAccount,
                });
                accessToken = tokenResponse.accessToken;
            } catch (e) {
                // Silent refresh failed (e.g. refresh token expired) — force interactive login
                reauthenticate();
                return;
            }
        }

        try {
            const headers = new Headers();
            headers.append("Authorization", `Bearer ${accessToken}`);
            if (data) headers.append('Content-Type', 'application/json');

            const options = {
                method: method,
                headers: headers,
                body: data ? JSON.stringify(data) : null,
            };

            setIsLoading(true);

            const httpResponse = await fetch(endpoint, options);

            if (httpResponse.status === 401) {
                // Token was rejected by server — force re-auth
                if (framedAuth?.accessToken) {
                    framedAuth.requestNewToken();
                } else {
                    reauthenticate();
                }
                return;
            }

            const response = await httpResponse.json();
            if (!httpResponse.ok) {
                throw response;
            }
            setData(response);
            setIsLoading(false);
            return response;
        } catch (e) {
            setError(e);
            setIsLoading(false);
            throw e;
        }
    };

    return {
        isLoading,
        error,
        data,
        execute: useCallback(execute, [msalError, framedAuth]), // to avoid infinite calls when inside a `useEffect`
    };
};

export default useFetchWithMsal;